export const COMPANY = {
  brand: "SunShell One",
  customerApp: "SunShell One",
  partnerApp: "SunShell One Partner",
  customerAppId: "com.sunshellone.customer",
  partnerAppId: "com.sunshellone.partnerapp",
  operator: "SUNSHELL TECHNOLOGY SOLUTIONS PRIVATE LIMITED",
  duns: "861420527",
  registeredAddress: "183 Netaji Subhas Road, Uttarpara, Hooghly, West Bengal 712258, India",
  contactAddress:
    "Office No. 714, 7th Floor, DLF Galleria, Block Number BG-8, Street Number 124, BG Block (Newtown), Action Area 1B, Newtown, West Bengal 700163, India",
  email: "info@sunshellpower.com",
  phone: "+91 76050 07177",
  lastUpdated: "23 September 2026",
};

const appData = [
  "Name, email address, phone number, login details, language, communication preferences, and support messages.",
  "Customer app data from SunShell One, including home, business, farm, project, roof, location, electricity-consumption, DISCOM, quotation, payment, subsidy, installation, warranty, and service-request details.",
  "Partner app data from SunShell One Partner, including partner onboarding, lead assignment, site surveys, customer-project details, quotations, tasks, installation workflow, GPS verification, completion OTP, commission, inventory, work, and job records.",
  "Photos, videos, documents, electricity bills, site-survey images, installation images, commissioning records, and related upload metadata submitted from either app.",
  "Device, app, browser, IP address, diagnostics, crash, notification-token, cookie, and usage data needed to run, secure, and improve the services.",
];

export const policies = {
  "privacy-policy": {
    title: "Privacy Policy",
    description: "How SunShell One and SunShell One Partner collect, use, share, protect, and delete personal data.",
    intro:
      "This Privacy Policy applies to the SunShell One customer app, the SunShell One Partner app, the SunShell One website, waitlist, solar journey, partner workflows, support channels, and related services.",
    sections: [
      {
        heading: "1. Who We Are",
        paragraphs: [
          `${COMPANY.customerApp} and ${COMPANY.partnerApp} are operated by ${COMPANY.operator}, ${COMPANY.registeredAddress}. D-U-N-S Number: ${COMPANY.duns}.`,
          `For privacy, consent withdrawal, grievance, or data deletion requests, contact ${COMPANY.email} or call ${COMPANY.phone}.`,
        ],
      },
      {
        heading: "2. Apps Covered",
        bullets: [
          `${COMPANY.customerApp}: customer app for solar enquiries, project tracking, quotations, payments, installation updates, support, and service requests. Android package / iOS bundle ID: ${COMPANY.customerAppId}.`,
          `${COMPANY.partnerApp}: partner and installer app for leads, site surveys, quotations, tasks, installation workflows, commissions, inventory, and related operations. Android package / iOS bundle ID: ${COMPANY.partnerAppId}.`,
          "This shared policy also covers the SunShell One website, waitlist, support channels, and related backend services connected to either app.",
        ],
      },
      {
        heading: "3. Data We Collect",
        paragraphs: ["We collect data you provide, data generated while using the services, and data needed for security and support."],
        bullets: appData,
      },
      {
        heading: "4. How We Use Data",
        bullets: [
          "For SunShell One customers: to create accounts, manage waitlist entries, process solar enquiries, track project journeys, generate quotations, coordinate payments, manage installations, provide support, send notifications, and handle service requests.",
          "For SunShell One Partner users: to manage partner onboarding, leads, site surveys, quotations, tasks, installation workflows, GPS verification, completion OTP, commissions, inventory, customer-project updates, and partner support.",
          "To run site surveys, upload and review project media, coordinate installation and QA, monitor solar journey progress, verify partner work, and improve product reliability.",
          "To verify identity, prevent fraud, protect systems, comply with Indian law, resolve disputes, maintain records, and send service communications.",
          "To send promotional messages only where permitted. You may opt out of non-essential marketing communications.",
        ],
      },
      {
        heading: "5. Legal Basis and Consent",
        paragraphs: [
          "We process personal data for lawful purposes, including consent, performance of requested services, legitimate business needs, and legal compliance under applicable Indian law, including the Digital Personal Data Protection Act, 2023 where applicable.",
          "Where permissions are optional, such as location, camera, photos, files, or push notifications, you can deny or revoke them through the app or device settings. Some features may stop working if the required permission is disabled.",
        ],
      },
      {
        heading: "6. Sharing of Data",
        bullets: [
          "With authorised SunShell teams, partners, installers, project managers, service personnel, customers, and partner-app users who need access for the relevant solar journey or partner workflow.",
          "With cloud hosting, storage, analytics, communication, payment, map/geocoding, notification, and support providers under confidentiality and security obligations.",
          "With banks, payment providers, lenders, government portals, DISCOMs, or subsidy-related parties when required for a requested solar project.",
          "With law enforcement, courts, regulators, or government authorities where legally required or needed to protect rights and safety.",
        ],
      },
      {
        heading: "7. Children",
        paragraphs: [
          "SunShell One is intended for home, business, farm, partner, and project users. It is not directed at children. If we learn that a child has provided personal data without appropriate consent, we will delete or restrict it as required by law.",
        ],
      },
      {
        heading: "8. Retention",
        paragraphs: [
          "We retain personal data only as long as necessary for the purposes described here, including active service delivery, legal, tax, accounting, warranty, subsidy, payment, security, audit, and dispute-resolution needs.",
          "After an account or deletion request is verified, we delete or anonymise eligible personal data from active systems, unless retention is required by law or for legitimate records.",
        ],
      },
      {
        heading: "9. Security",
        bullets: [
          "HTTPS/TLS for data in transit where supported.",
          "Role-based access controls for operational systems.",
          "Limited access to project records based on role and business need.",
          "Monitoring, backups, and operational safeguards to investigate errors, misuse, and security incidents.",
        ],
      },
      {
        heading: "10. Your Rights",
        paragraphs: [
          `Subject to verification and applicable law, you may request access, correction, consent withdrawal, grievance redressal, or deletion by emailing ${COMPANY.email}. We may ask for information needed to verify that the request comes from the account holder or an authorised representative.`,
        ],
      },
      {
        heading: "11. Changes and Contact",
        paragraphs: [
          `We may update this Policy when laws, services, or business practices change. The latest version will appear on this page. Contact: ${COMPANY.email}; ${COMPANY.contactAddress}.`,
        ],
      },
    ],
  },
  "terms-and-conditions": {
    title: "Terms and Conditions",
    description: "Terms for using the SunShell One customer app, SunShell One Partner app, website, solar journey, partner tools, and services.",
    intro:
      "These Terms govern your access to the SunShell One customer app, the SunShell One Partner app, the SunShell One website, waitlist, solar journey, partner workflows, project tools, support channels, and related services.",
    sections: [
      {
        heading: "1. Contracting Entity",
        paragraphs: [
          `${COMPANY.customerApp} and ${COMPANY.partnerApp} are provided by ${COMPANY.operator}, ${COMPANY.registeredAddress}. D-U-N-S Number: ${COMPANY.duns}.`,
          `Questions about these Terms can be sent to ${COMPANY.email}.`,
        ],
      },
      {
        heading: "2. App Roles",
        paragraphs: [
          `${COMPANY.customerApp} is intended for customers who use the app for solar enquiries, project tracking, quotations, payments, installation updates, support, and service requests. ${COMPANY.partnerApp} is intended for partners, installers, field teams, and authorised business users who use the app for leads, site surveys, quotations, tasks, installation workflows, commissions, inventory, and related operations.`,
          `For clarity, ${COMPANY.customerApp} uses app identifier ${COMPANY.customerAppId}, and ${COMPANY.partnerApp} uses app identifier ${COMPANY.partnerAppId}.`,
        ],
      },
      {
        heading: "3. Eligibility and Accounts",
        bullets: [
          "You must provide accurate information and keep account credentials secure.",
          "You are responsible for activity under your account unless caused by our breach or a security failure outside your control.",
          "Partners, installers, employees, and representatives must use the services only for authorised business purposes.",
        ],
      },
      {
        heading: "4. Solar Information, Quotes, and Subsidies",
        bullets: [
          "Savings, generation, payback, subsidy, financing, and system-size estimates are indicative and depend on site conditions, consumption, tariffs, DISCOM rules, government schemes, product selection, and approvals.",
          "A final quote, scope, warranty, timeline, payment plan, and installation obligations must be confirmed in separate project documents or written communications.",
          "Subsidy approval, loan approval, grid connection, net metering, and government or DISCOM processing are not guaranteed by SunShell One.",
        ],
      },
      {
        heading: "5. User Content and Site Data",
        paragraphs: [
          "You confirm that you have the right to upload bills, photos, videos, documents, addresses, project details, partner documents, installation records, and other content you provide through either app. You permit us to use that content to provide and support the requested services.",
        ],
      },
      {
        heading: "6. Payments and Cancellations",
        paragraphs: [
          "Fees, milestones, refunds, cancellations, and taxes are governed by the quote, invoice, project agreement, app store terms, payment provider terms, or written commercial arrangement applicable to your purchase.",
          "Do not make payment unless the amount, beneficiary, and purpose are clear to you. Contact us if you suspect fraud or an unauthorised payment request.",
        ],
      },
      {
        heading: "7. Acceptable Use",
        bullets: [
          "Do not misuse the services, upload unlawful or infringing content, attempt unauthorised access, reverse engineer restricted systems, disrupt security, or use the services for fraudulent activity.",
          "Do not submit false project, customer, partner, payment, subsidy, or installation information.",
        ],
      },
      {
        heading: "8. Intellectual Property",
        paragraphs: [
          "The SunShell One and SunShell One Partner names, software, website, app designs, text, visuals, workflows, and technology are owned by or licensed to us. You receive only a limited, revocable, non-transferable right to use the services for their intended purpose.",
        ],
      },
      {
        heading: "9. Service Availability",
        paragraphs: [
          "We aim to keep the services reliable, but they may be interrupted for maintenance, updates, network issues, third-party provider failures, security reasons, or events outside our reasonable control.",
        ],
      },
      {
        heading: "10. Liability",
        paragraphs: [
          "To the maximum extent permitted by law, we are not liable for indirect, incidental, special, punitive, or consequential losses. Nothing in these Terms limits liability that cannot legally be limited.",
        ],
      },
      {
        heading: "11. Governing Law and Disputes",
        paragraphs: [
          "These Terms are governed by the laws of India. Courts with competent jurisdiction in West Bengal, India will have jurisdiction, unless applicable consumer law requires otherwise.",
        ],
      },
      {
        heading: "12. Updates",
        paragraphs: [
          "We may update these Terms from time to time. Continued use of the services after an update means you accept the updated Terms.",
        ],
      },
    ],
  },
  "delete-account": {
    title: "Account and Data Deletion Policy",
    description: "How to request deletion of your SunShell One or SunShell One Partner account and associated personal data.",
    intro:
      "This page explains how users can request deletion of a SunShell One customer account or a SunShell One Partner account and associated data, including when either app has been uninstalled.",
    sections: [
      {
        heading: "1. How to Request Deletion",
        paragraphs: [
          `Email ${COMPANY.email} from the email address linked to your SunShell One or SunShell One Partner account with the subject line "Account deletion request".`,
          "Include your full name, registered phone number, account email, app name, and whether the account is for a customer, partner, installer, field team member, or business user. Do not send passwords, OTPs, card numbers, or sensitive identity documents unless we specifically request a secure verification step.",
          `This deletion process covers ${COMPANY.customerApp} (${COMPANY.customerAppId}) and ${COMPANY.partnerApp} (${COMPANY.partnerAppId}).`,
        ],
      },
      {
        heading: "2. Verification",
        paragraphs: [
          "We verify deletion requests to protect accounts from unauthorised deletion. We may ask for reasonable information to confirm that the request comes from the account holder or an authorised representative.",
        ],
      },
      {
        heading: "3. What We Delete",
        bullets: [
          "Account profile data, contact details, preferences, and authentication records that are no longer needed.",
          "Eligible customer-app data, including uploaded photos, videos, bills, documents, project notes, support messages, and app data linked only to your account.",
          "Eligible partner-app data, including partner profile details, task records, site-survey uploads, installation workflow data, and partner support messages linked only to your account.",
          "Device tokens and optional permissions data, such as push-notification tokens and stored location data, where no longer required.",
        ],
      },
      {
        heading: "4. What We May Retain",
        bullets: [
          "Invoices, payment, tax, accounting, subsidy, warranty, installation, commissioning, and legal records that must be retained under law or for legitimate records.",
          "Project records needed by other authorised users, customers, partners, installers, DISCOM/subsidy processes, warranty support, safety checks, or dispute resolution.",
          "Security logs, fraud-prevention records, backups, and audit records for limited periods.",
          "Aggregated or anonymised information that no longer identifies you.",
        ],
      },
      {
        heading: "5. Timeline",
        paragraphs: [
          "After verification, we aim to disable account access promptly and complete deletion or anonymisation of eligible personal data from active systems within 30 days, unless a longer period is required for legal, security, payment, warranty, subsidy, or dispute-resolution reasons.",
          "Backup copies may persist for a limited period until they are overwritten through normal backup cycles.",
        ],
      },
      {
        heading: "6. Effect of Deletion",
        paragraphs: [
          "After deletion, you may lose access to your customer or partner account, saved project journey, uploaded documents, service history, quotes, tasks, notifications, partner tools, and app features. Some project or transaction records may remain available to SunShell or authorised project stakeholders where legally or operationally required.",
        ],
      },
      {
        heading: "7. Contact",
        paragraphs: [
          `Deletion and privacy requests: ${COMPANY.email}. Phone: ${COMPANY.phone}. Postal contact: ${COMPANY.operator}, ${COMPANY.registeredAddress}.`,
        ],
      },
    ],
  },
};

policies["delete-policy"] = policies["delete-account"];
